// src/pages/lkp/PublishReferenceButton.tsx
//
// One action on the lookup page that propagates a reference-data change to BOTH
// downstream artifacts. The lookup layer is the single reference authority, so after
// the SME accepts/rejects a lookup, promotes values, or edits canonical values, this
// pushes the change through:
//   • Enterprise Knowledge — re-projects READY reference concepts as reference domains.
//   • Business Control      — re-guards against the refreshed EKD: remaps controls whose
//                             domain drifted (by field), drops controls on now-ungoverned
//                             fields (measures), re-lanes mis-flagged controls, recounts lanes.
//
// Drop-in: <PublishReferenceButton entityId={entityId} onDone={reload} />
// Self-contained (own styles, own state). Matches the lookup theme tokens.

import React, { useState } from "react";
import { lookup_publishReference, type PublishReferenceResult } from "../api/lkpAPI";

interface Props {
  entityId: string;
  onDone?: (r: PublishReferenceResult) => void;
  className?: string;
}

const fmt = (n?: number) => (typeof n === "number" ? n.toLocaleString() : "—");

export default function PublishReferenceButton({ entityId, onDone, className }: Props) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<PublishReferenceResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setBusy(true); setErr(null); setRes(null);
    try {
      const r = await lookup_publishReference(entityId);
      setRes(r);
      onDone?.(r);
    } catch (e: any) {
      setErr(e?.detail || e?.message || "Couldn't reach the server. Check the connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const ekd = res?.enterprise_knowledge;
  const bc = res?.business_control;
  const bcMoved = bc && !bc.skipped && bc.controls_before !== bc.controls_after;
  const bcChanged = bc && !bc.skipped && ((bc.remapped || 0) + (bc.dropped || 0) + (bc.reflagged || 0) > 0);

  return (
    <div className={"prb " + (className || "")}>
      <style>{CSS}</style>

      <button className="prb-btn" onClick={run} disabled={busy}
        title="Push the current reference data into Enterprise Knowledge and Business Control">
        <span className="prb-ico" aria-hidden>{busy ? "↻" : "↻"}</span>
        {busy ? "Refreshing…" : "Refresh EKD & BC"}
      </button>

      {err && (
        <div className="prb-panel err" role="alert">
          <div className="prb-panel-h">Refresh didn't complete</div>
          <div className="prb-panel-b">{err}</div>
          <button className="prb-x" onClick={() => setErr(null)}>Dismiss</button>
        </div>
      )}

      {res && (
        <div className="prb-panel ok" role="status">
          <div className="prb-panel-h">Reference published downstream</div>
          <div className="prb-row">
            <span className="prb-tag ekd">Enterprise Knowledge</span>
            <span className="prb-txt">{fmt(ekd?.reference_domains)} domains · {fmt(ekd?.values)} values</span>
          </div>
          <div className="prb-row">
            <span className="prb-tag bc">Business Control</span>
            {bc?.skipped ? (
              <span className="prb-txt muted">Not generated yet — nothing to re-guard.</span>
            ) : (
              <span className="prb-txt">
                {bcMoved
                  ? <><b>{fmt(bc?.controls_before)} → {fmt(bc?.controls_after)}</b> controls</>
                  : <><b>{fmt(bc?.controls_after)}</b> controls (no change)</>}
                {bcChanged && (
                  <span className="prb-sub">
                    {" · "}
                    {(bc?.remapped || 0) > 0 && <>{bc?.remapped} remapped</>}
                    {(bc?.remapped || 0) > 0 && ((bc?.dropped || 0) > 0 || (bc?.reflagged || 0) > 0) && ", "}
                    {(bc?.dropped || 0) > 0 && <>{bc?.dropped} dropped</>}
                    {(bc?.dropped || 0) > 0 && (bc?.reflagged || 0) > 0 && ", "}
                    {(bc?.reflagged || 0) > 0 && <>{bc?.reflagged} re-laned</>}
                  </span>
                )}
              </span>
            )}
          </div>
          <button className="prb-x" onClick={() => setRes(null)}>Dismiss</button>
        </div>
      )}
    </div>
  );
}

const CSS = `
.prb{--ink:#14202b;--surface:#fff;--surface2:#f7f9f7;--line:#dde3df;--tx:#182430;--tx2:#5d6d68;--tx3:#8a9791;
  --gov:#0e8a6e;--gov-bg:#e2f2ec;--warn:#c23c28;--warn-bg:#f7e0da;--infer:#4a51c0;
  position:relative;display:inline-block;font-family:'Inter',system-ui,sans-serif;color:var(--tx)}
.prb *{box-sizing:border-box}
.prb-btn{display:inline-flex;align-items:center;gap:7px;border:1px solid var(--ink);background:var(--ink);color:#fff;
  border-radius:8px;padding:8px 14px;font-size:12.5px;font-weight:600;cursor:pointer;font-family:inherit}
.prb-btn:hover{background:#20303e}
.prb-btn:disabled{opacity:.55;cursor:default}
.prb-ico{font-family:'IBM Plex Mono',monospace;font-size:13px;line-height:1}
.prb-btn:disabled .prb-ico{animation:prb-spin .9s linear infinite}
@keyframes prb-spin{to{transform:rotate(360deg)}}
.prb-panel{position:absolute;right:0;top:calc(100% + 6px);z-index:30;min-width:290px;max-width:360px;
  background:var(--surface);border:1px solid var(--line);border-radius:11px;padding:13px 14px 12px;
  box-shadow:0 10px 30px rgba(20,32,43,.14)}
.prb-panel.ok{border-top:3px solid var(--gov)}
.prb-panel.err{border-top:3px solid var(--warn)}
.prb-panel-h{font-size:13px;font-weight:700;margin-bottom:9px}
.prb-panel.err .prb-panel-h{color:var(--warn)}
.prb-panel-b{font-size:12.5px;color:var(--tx2);line-height:1.45}
.prb-row{display:flex;align-items:baseline;gap:9px;margin:7px 0}
.prb-tag{flex:none;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;
  padding:3px 7px;border-radius:5px;font-family:'IBM Plex Mono',monospace}
.prb-tag.ekd{background:#eef0fb;color:var(--infer)}
.prb-tag.bc{background:var(--gov-bg);color:var(--gov)}
.prb-txt{font-size:12.5px;color:var(--tx);line-height:1.4}
.prb-txt.muted{color:var(--tx3)}
.prb-txt b{font-weight:700}
.prb-sub{color:var(--tx2)}
.prb-x{margin-top:10px;border:1px solid var(--line);background:var(--surface2);color:var(--tx2);
  border-radius:7px;padding:6px 11px;font-size:11.5px;font-weight:600;cursor:pointer;font-family:inherit}
.prb-x:hover{border-color:var(--tx2);color:var(--tx)}
@media(max-width:620px){.prb-panel{right:auto;left:0;min-width:250px}}
`;
